import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  FiHome,
  FiPlusSquare,
  FiLogIn,
  FiUserPlus,
  FiChevronDown,
  FiChevronUp,
  FiTrendingUp,
  FiClock,
  FiDollarSign,
  FiGrid,
  FiChevronRight,
  FiMenu
} from "react-icons/fi";
import { 
  MdDirectionsCar,
  MdOutlineTwoWheeler,
  MdOutlineCarRental,
  MdOutlineElectricCar
} from "react-icons/md";
import { 
  FaMotorcycle,
  FaCarSide,
  FaGasPump
} from "react-icons/fa";
import { GiCarKey, GiSteeringWheel } from "react-icons/gi";
import UserProfile from "./UserProfile";
import { onAuthStateChanged, getAuth } from "firebase/auth";

const MenuLinks = ({ isOpen, onClose }) => {
  const [loginState, setLoginState] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setLoginState(!!user);
    });

    // Set active item based on current route
    const path = location.pathname;
    if (path.includes("/category/sale")) setActiveItem('sale');
    else if (path.includes("/category/rent")) setActiveItem('rent');
    else if (path.includes("/category/ride")) setActiveItem('ride');
    else if (path.includes("/featured-bikes")) setActiveItem('bikes');
    else if (path.includes("/create-listing")) setActiveItem('create');
    else if (path.includes("/feed")) setActiveItem('feed');
    else if (path === "/") setActiveItem('home');

    return () => unsubscribe();
  }, [location.pathname]);

  const handleItemClick = (itemName) => {
    setActiveItem(itemName);
    setShowVehicleDropdown(false);
    if (onClose) onClose();
  };

  const toggleVehicleDropdown = (e) => {
    e.stopPropagation();
    setShowVehicleDropdown(!showVehicleDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowVehicleDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const menuItems = [
    {
      name: 'home',
      label: 'Home',
      path: '/',
      icon: <FiHome className="text-xl" />
    },
    {
      name: 'vehicles',
      label: 'Vehicles',
      isDropdown: true,
      icon: <MdDirectionsCar className="text-xl" />
    },
    {
      name: 'feed',
      label: 'Requests',
      path: '/feed',
      icon: <FiGrid className="text-xl" />
    },
    {
      name: 'create',
      label: 'List',
      path: '/create-listing',
      icon: <FiPlusSquare className="text-xl" />
    }
  ];

  const vehicleOptions = [
    {
      name: 'sale',
      label: 'Cars for Sale',
      path: '/category/sale',
      icon: <GiCarKey className="text-blue-500" />,
      description: 'Buy premium vehicles'
    },
    {
      name: 'rent',
      label: 'Rent a Car',
      path: '/category/rent',
      icon: <MdOutlineCarRental className="text-emerald-500" />,
      description: 'Short-term rentals'
    },
    {
      name: 'ride',
      label: 'Book a Ride',
      path: '/category/ride',
      icon: <GiSteeringWheel className="text-purple-500" />,
      description: 'Carpool services'
    },
    {
      name: 'bikes',
      label: 'Motorcycles',
      path: '/featured-bikes',
      icon: <MdOutlineTwoWheeler className="text-red-500" />,
      description: 'Bikes & scooters'
    }
  ];

  const getBottomBarItemClasses = (itemName) => {
    const isActive = activeItem === itemName;
    
    if (isActive) {
      return "text-blue-600";
    }
    return "text-gray-500 hover:text-blue-500";
  };

  // Mobile Bottom Bar Component
  const MobileBottomBar = () => (
    <>
      {/* Bottom Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200/50 shadow-lg z-50">
        <div className="flex items-center justify-around px-2 py-1">
          {menuItems.map((item) => (
            <div key={item.name} className="relative">
              {item.isDropdown ? (
                // Vehicles Dropdown Trigger
                <button
                  onClick={toggleVehicleDropdown}
                  className={`flex flex-col items-center py-1 px-3 rounded-lg transition-all duration-200 min-w-[60px] ${getBottomBarItemClasses('vehicles')}`}
                >
                  <div className="relative">
                    {item.icon}
                    {showVehicleDropdown && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    )}
                  </div>
                  <span className="text-[10px] font-medium mt-0.5">{item.label}</span>
                </button>
              ) : (
                <Link
                  to={item.path}
                  className={`flex flex-col items-center py-1 px-3 rounded-lg transition-all duration-200 min-w-[60px] ${getBottomBarItemClasses(item.name)}`}
                  onClick={() => handleItemClick(item.name)}
                >
                  {item.icon}
                  <span className="text-[10px] font-medium mt-0.5">{item.label}</span>
                </Link>
              )}
            </div>
          ))}
          
          {/* Auth/Profile Section */}
          <div className="flex flex-col items-center py-1 px-3 min-w-[60px]">
            {!loginState ? (
              <Link
                to="/sign-in"
                className="flex flex-col items-center text-gray-500 hover:text-blue-500 transition-colors"
                onClick={() => handleItemClick('signin')}
              >
                <FiLogIn className="text-xl" />
                <span className="text-[10px] font-medium mt-0.5">Sign In</span>
              </Link>
            ) : (
              <div className="flex flex-col items-center">
                <UserProfile />
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Dropdown Overlay */}
        {showVehicleDropdown && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setShowVehicleDropdown(false)}
            />
            
            {/* Dropdown Menu */}
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden z-50 animate-slideUp">
              <div className="p-3 max-h-[60vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Vehicle Categories</h3>
                  <button 
                    onClick={() => setShowVehicleDropdown(false)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <FiChevronDown className="text-gray-500" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {vehicleOptions.map((option) => (
                    <Link
                      key={option.name}
                      to={option.path}
                      className={`flex flex-col items-center p-3 rounded-xl transition-all duration-200 border-2 ${
                        activeItem === option.name
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-transparent hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleItemClick(option.name)}
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-2">
                        {option.icon}
                      </div>
                      <span className="text-xs font-medium text-gray-900 text-center">{option.label}</span>
                      <span className="text-[10px] text-gray-500 text-center">{option.description}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Spacer for bottom bar */}
      <div className="md:hidden h-16"></div>
    </>
  );

  return (
    <>
      {/* Desktop Menu - Completely hidden on mobile */}
      <div className="hidden md:block w-full md:w-auto">
        <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-1">
          {menuItems.map((item) => (
            <div key={item.name} className="relative">
              {item.isDropdown ? (
                // Vehicles Dropdown for Desktop
                <div className="relative dropdown-container">
                  <button
                    onMouseEnter={() => setHoveredItem('vehicles')}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={toggleVehicleDropdown}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                      activeItem === 'vehicles'
                        ? 'text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25'
                        : hoveredItem === 'vehicles'
                        ? 'text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 shadow-md'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:shadow-md'
                    } min-w-[120px]`}
                  >
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {showVehicleDropdown ? (
                      <FiChevronUp className="text-sm ml-1" />
                    ) : (
                      <FiChevronDown className="text-sm ml-1" />
                    )}
                  </button>

                  {showVehicleDropdown && (
                    <div className="absolute z-50 mt-2 w-64 origin-top-right rounded-xl bg-white/95 backdrop-blur-sm shadow-2xl border border-gray-200/50 focus:outline-none transition-all duration-200 animate-fadeIn">
                      <div className="p-2">
                        {vehicleOptions.map((option) => (
                          <Link
                            key={option.name}
                            to={option.path}
                            onMouseEnter={() => setHoveredItem(option.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                            className={`flex items-center p-3 rounded-lg transition-all duration-200 mb-1 last:mb-0 ${
                              activeItem === option.name
                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100'
                                : 'hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
                            }`}
                            onClick={() => handleItemClick(option.name)}
                          >
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                              {option.icon}
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {option.label}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {option.description}
                              </p>
                            </div>
                            <FiChevronRight className="text-gray-400 ml-2" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Regular Menu Item for Desktop
                <Link
                  to={item.path}
                  onMouseEnter={() => setHoveredItem(item.name)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                    activeItem === item.name
                      ? 'text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25'
                      : hoveredItem === item.name
                      ? 'text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 shadow-md'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:shadow-md'
                  } min-w-[100px]`}
                  onClick={() => handleItemClick(item.name)}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              )}
            </div>
          ))}

          {/* Auth Buttons for Desktop */}
          {!loginState ? (
            <div className="flex items-center space-x-2 mt-2 md:mt-0">
              <Link
                to="/sign-in"
                onMouseEnter={() => setHoveredItem('signin')}
                onMouseLeave={() => setHoveredItem(null)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:border-gray-400 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-300 shadow-sm hover:shadow-md"
                onClick={() => handleItemClick('signin')}
              >
                <FiLogIn className="text-lg" />
                <span>Sign In</span>
              </Link>
              <Link
                to="/sign-up"
                onMouseEnter={() => setHoveredItem('signup')}
                onMouseLeave={() => setHoveredItem(null)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 shadow-md relative overflow-hidden group"
                onClick={() => handleItemClick('signup')}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                <FiUserPlus className="text-lg relative z-10" />
                <span className="relative z-10">Sign Up</span>
              </Link>
            </div>
          ) : (
            <div className="mt-2 md:mt-0">
              <UserProfile />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Bar */}
      <MobileBottomBar />
    </>
  );
};

export default MenuLinks;